<template>
  <div class="player-slot-card" :class="[`team-${team.toLowerCase()}`, { filled: !!player, empty: !player }]">
    <!-- Player Content -->
    <div v-if="player" class="ps-content">
      <div class="ps-avatar">
        <img
          v-if="player.profile_picture"
          :src="`/images/profiles/${player.profile_picture}.png`"
          :alt="displayName"
        />
        <div v-else class="ps-initials">{{ initials }}</div>
      </div>
      <div class="ps-info">
        <span class="ps-name">
          {{ displayName }}
          <span v-if="isCurrentUser" class="ps-you">(You)</span>
          <span v-if="player.is_bot" class="ps-bot">Bot</span>
        </span>
        <span class="ps-seat">Seat {{ seat + 1 }}</span>
      </div>
      <!-- Host Controls -->
      <button
        v-if="isHost && !isCurrentUser && isWaiting"
        class="ps-kick-btn"
        @click="$emit('kick', player.id)"
        title="Remove player"
      >
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>

    <!-- Empty Slot -->
    <div v-else class="ps-empty">
      <div class="ps-empty-avatar">
        <span class="material-symbols-outlined">person_add</span>
      </div>
      <div class="ps-info">
        <span class="ps-name ps-waiting">Waiting...</span>
        <span class="ps-seat">Seat {{ seat + 1 }}</span>
      </div>
      <!-- Add Bot Button -->
      <button
        v-if="isHost && isWaiting"
        class="ps-add-bot-btn"
        @click="$emit('add-bot')"
      >
        <span class="material-symbols-outlined">smart_toy</span>
        Add Bot
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Player {
  id: string
  username: string
  display_name?: string
  profile_picture?: string
  seat: number
  team: string
  is_bot: boolean
}

const props = defineProps<{
  seat: number
  player: Player | undefined
  isCurrentUser: boolean
  isHost: boolean
  isWaiting: boolean
  team: 'A' | 'B'
}>()

defineEmits<{
  (e: 'kick', playerId: string): void
  (e: 'add-bot'): void
}>()

const displayName = computed(() => {
  if (!props.player) return ''
  if (props.player.is_bot) return props.player.username
  return props.player.display_name || props.player.username || 'Player'
})

const initials = computed(() => {
  if (!props.player) return '?'
  const name = displayName.value
  const parts = name.split(' ')
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})
</script>

<style scoped>
.player-slot-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 20px 24px;
  min-width: 0;
  min-height: 90px;
  transition: all 0.3s ease;
}

.player-slot-card.team-a {
  border-left: 4px solid var(--team-1-color, #c9a227);
}

.player-slot-card.team-b {
  border-left: 4px solid var(--team-2-color, #c94a4a);
}

.player-slot-card.filled {
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.1) 0%, rgba(201, 162, 39, 0.03) 100%);
  border-color: rgba(201, 162, 39, 0.25);
}

.player-slot-card.empty {
  border-style: dashed;
  opacity: 0.8;
}

.player-slot-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

/* Filled slot content */
.ps-content {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.ps-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(201, 162, 39, 0.4);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}

.ps-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.3), rgba(201, 162, 39, 0.1));
  color: var(--accent-primary, #c9a227);
  font-weight: 700;
  font-size: 20px;
}

.ps-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.ps-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ps-you {
  font-size: 14px;
  color: var(--accent-primary, #c9a227);
  font-weight: 500;
}

.ps-bot {
  font-size: 11px;
  padding: 3px 8px;
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border-radius: 4px;
  font-weight: 600;
}

.ps-seat {
  font-size: 12px;
  color: var(--text-muted, #8899a6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ps-kick-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(201, 74, 74, 0.9);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.player-slot-card:hover .ps-kick-btn {
  opacity: 1;
}

.ps-kick-btn:hover {
  background: #c94a4a;
  transform: scale(1.1);
}

.ps-kick-btn .material-symbols-outlined {
  font-size: 16px;
}

/* Empty slot content */
.ps-empty {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ps-empty-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.2);
  color: var(--text-muted, #8899a6);
  flex-shrink: 0;
}

.ps-empty-avatar .material-symbols-outlined {
  font-size: 24px;
  opacity: 0.5;
}

.ps-waiting {
  color: var(--text-muted, #8899a6);
  font-style: italic;
}

.ps-add-bot-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  color: #22c55e;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.ps-add-bot-btn:hover {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.15) 100%);
  border-color: rgba(34, 197, 94, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.2);
}

.ps-add-bot-btn .material-symbols-outlined {
  font-size: 20px;
}

/* Material icons */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>
